import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import chunk from 'lodash/chunk';
import { compose, onlyUpdateForKeys } from 'recompose';
import styled, { StylesProps } from '../../../lib/styles';
import { Article } from '../../../models/article';
import Typography from '../../../components/presentational/Typography/Typography';
import StoryByline from '../../../components/presentational/StoryByline';
import MainImage from '../MainImage';
import Video from '../Video';
import Spacer from '../../../components/presentational/Spacer';
import Slider from '../../../components/presentational/Slider';
import Ad, { Ads } from '../../../components/presentational/Ad';
import ArticleSharing from '../ArticleSharing';
import BlockUpdates from '../../../behaviour/BlockUpdates';
import memoize from '../../../lib/selectorUtils';
import {
  AUTHOR_CLICKED,
  CATEGORY_CLICKED,
  CATEGORY_CLOUD_CLICKED,
  SHARE,
} from '../lib/ga';
import { trackEvent } from '../../../lib/ga';

import standardArticleLayoutStyles from './StandardArticleLayout_styles';
import { suggestedArticlesSelector } from '../store/selectors';
import { Account } from '../../../models/user';
import AdhesionAd from '../AdhesionAd';
import { raiseEvent, SIGNUP_ARTICLE_COLLECTION_CHALLENGE, STANDARD_ARTICLE_VIEW } from '../../../lib/gtm';
import theme from '../../../lib/theme';
import { TrackingDataState } from '../../../store/reducers/trackingData';
import { trackingDataSelector } from '../../../store/selectors';
import MembershipChallenge from '../../../components/presentational/MembershipChallenge';
const { layoutWidths } = theme;

export interface StandardArticleLayoutProps {
  ads: Ads;
  suggestedArticles: Article[];
  article: Article;
  isFirstPartVisible?: boolean;
  user?: Account | false;
  hideComments?: boolean;
  hideStickyRecirc?: boolean;
  hideAds: boolean;
  showLinkbyPubFeed: boolean;
}

const HIDDEN_ADHESION = process.env.HIDDEN_ADHESION;

const RECIRCULATED_ARTICLE = 'RECIRCULATED_ARTICLE';
const NATIVE_AD = 'NATIVE_AD';
const VIDEO_AD = 'VIDEO_AD';
const DISPLAY_AD = 'DISPLAY_AD';

/**
 * The native comments section is appended by the startsat60v7 theme as the FINAL
 * element of the article `content` array (render.php: `$article['content'][] =
 * Comments::render($post)`), so it flows through this chunk/separator loop like any
 * other content block, identified by this class on its wrapper <section>. We stop
 * placing in-content ads from the chunk that carries it onward, so no ad renders
 * after the comment section is injected. Kept in sync with the theme's markup —
 * see web/app/themes/startsat60v7/lib/comments.php.
 */
const COMMENTS_BLOCK_MARKER = 'sas-comments';

type ChunkSeperator =
  | typeof RECIRCULATED_ARTICLE
  | typeof NATIVE_AD
  | typeof VIDEO_AD
  | typeof DISPLAY_AD;

/**
 * What renders after each chunk of the second article part, in order.
 *
 * Index 0 was the 500px parallax ScrollAd (standard_scrollad) until 2026-07. It is
 * replaced by the out-stream video unit (standard_inarticle_video, 180-250px tall)
 * to cut in-article scroll cost: no paying line items targeted the scrollad unit
 * (verified in GAM 2026-07-20) and its post-AdSparc eCPM (~A$1.86) no longer
 * out-earned the other in-content units. The unit's 300x250 fallback size fills
 * from display demand whenever video demand does not bid. When video is disabled
 * page-wide, chunk 0 falls back to a recirculated article — see chunksWithSeparators.
 *
 * The display units stay at chunks 1-2 (ordered by revenue per viewable
 * impression); the native slots — which earn ~A$0.5 eCPM — stay last.
 */
const chunkSeparatorOrder: ChunkSeperator[] = [
  VIDEO_AD, // standard_inarticle_video (out-stream; falls back to recirc when video is off)
  DISPLAY_AD, // standard_inarticle1
  DISPLAY_AD, // standard_inarticle2
  RECIRCULATED_ARTICLE,
  NATIVE_AD, // standard_inarticle_native
  NATIVE_AD, // standard_enterprise_inarticle_native
];

interface ChunkWithSeparator {
  pars: string[];
  separator: ChunkSeperator;
  separatorIndex: number;
}

interface ChunkWalker {
  chunks: ChunkWithSeparator[];
  lastIndices: {
    [key: string]: number;
  };
}

const chunksWithSeparators = (
  chunks: string[][],
  videoEnabled: boolean
): ChunkWithSeparator[] =>
  chunks.reduce(
    (
      { chunks, lastIndices }: ChunkWalker,
      chunk: string[],
      i: number
    ): ChunkWalker => {
      const requestedSeparator = chunkSeparatorOrder[i];
      // When video is off (hide_teads), fall back to a recirculated article rather
      // than DISPLAY_AD: both display slots are already spent on chunks 1 and 2, so
      // a third DISPLAY_AD would resolve to a non-existent `standard_inarticle3`.
      const separator =
        (requestedSeparator === VIDEO_AD
          ? videoEnabled
            ? VIDEO_AD
            : RECIRCULATED_ARTICLE
          : requestedSeparator) || RECIRCULATED_ARTICLE;
      const separatorIndex = lastIndices[separator] + 1;

      return {
        chunks: [...chunks, { pars: chunk, separator, separatorIndex }],

        lastIndices: {
          ...lastIndices,

          [separator]: separatorIndex,
        },
      };
    },

    {
      chunks: [],
      lastIndices: {
        [RECIRCULATED_ARTICLE]: -1,
        [NATIVE_AD]: -1,
        [VIDEO_AD]: -1,
        [DISPLAY_AD]: -1,
      },
    }
  ).chunks;

const SPLIT_AFTER = 4;
/**
 * In-content ad cadence for the article BODY (part 2). The lede split stays at
 * SPLIT_AFTER=4 — that is what keeps standard_billboard at its paragraph-4
 * position — so the resulting ad rhythm is: billboard after paragraph 4, then one
 * in-content unit every 8 paragraphs (out-stream video ~12, inarticle1 ~20,
 * inarticle2 ~28, ...). Raised from 4 (2026-07) to halve in-content ad density:
 * article readability was judged too ad-heavy with a unit every 4 paragraphs.
 */
const PARAGRAPHS_PER_CHUNK = 8;

/**
 * Whether an article is long enough to be split into a lede + body.
 *
 * EVERY in-content ad is gated on `!isFirstPart`, so an article that does not split
 * renders no in-content ads at all — see the `parts` comment below.
 */
export const articleSplits = (content: string[]) => content.length > SPLIT_AFTER;

const StandardArticleLayout = ({
  ads,
  suggestedArticles,
  //  @ts-ignore
  hideComments,
  //  @ts-ignore
  hideStickyRecirc,
  hideAds = false,
  showLinkbyPubFeed = false,
  article: {
    //  @ts-ignore
    article_question,
    title,
    link,
    author,
    categories,
    category,
    category_link,
    created,
    youtube_video,
    large_image,
    image_caption,
    content,
    gallery,
    slug,
    //  @ts-ignore
    section = 'Discover',
  },
  styles,
}: StandardArticleLayoutProps & StylesProps<typeof standardArticleLayoutStyles>) => {

  const trackingSelectorData: TrackingDataState = useSelector(trackingDataSelector);

  // Debugging
  // useEffect(() => {
  //   console.log('StandardArticleLayout trackingSelectorData', { trackingSelectorData });
  // }, [trackingSelectorData]);

  useEffect(() => {
    // console.log('StandardArticleLayout', { ads });
    // const { targeting: defaultAdTargeting } = ads.standard_enterprise_billboard || {};
    raiseEvent({
      ...STANDARD_ARTICLE_VIEW,
      ...{
        sasEventType: `STANDARD_ARTICLE_VIEW: ${category}`,
      },
    });
  }, []);

  /**
   * The lede (the first SPLIT_AFTER paragraphs) is deliberately ad-free, so the
   * article is split and every in-content ad renders in the SECOND part.
   *
   * This used to require 8 paragraphs. Below that the article stayed a SINGLE part —
   * which made that part both the first AND the last at once. Since every in-content
   * ad is gated on `!isFirstPart` (ScrollAd, display, native, video) and the billboard
   * was gated on `!isSecondPart`, a short article rendered NO in-content ads AT ALL.
   *
   * Measured against production: 14.2% of standard articles (19/134 sampled). The cliff
   * was exact — every article with <= 7 blocks rendered 0 ads, every article with >= 8
   * rendered at least 2. Those articles still requested standard_billboard and
   * standard_scrollad (both predefined) for divs that never existed, so the bug was
   * also inflating ad requests and depressing fill — see AD_STACK.md §4.
   *
   * Now we split whenever there is any content after the lede.
   */
  const parts = articleSplits(content)
    ? [content.slice(0, SPLIT_AFTER), content.slice(SPLIT_AFTER)]
    : [content];

  return (
    <Spacer space={'s'} maxWidth={layoutWidths.contentPane} align={'center'}
      className={`standard-layout`}
    >
      {parts.map((pars: string[], i: number) => {
        const isFirstPart = i === 0;
        const isSecondPart = i === parts.length - 1;
        const chunks = chunk(pars, PARAGRAPHS_PER_CHUNK);

        // The comments section is the last content block (appended by the theme).
        // Find the chunk it lands in so we can stop rendering ads from there on.
        const commentsChunkIndex = chunks.findIndex((chunkPars: string[]) =>
          chunkPars.some(
            par => typeof par === 'string' && par.includes(COMMENTS_BLOCK_MARKER)
          )
        );

        return (
          <Spacer key={i} width={layoutWidths.contentPane} space={'m'}>
            <Spacer relative row space={'m'} maxWidth={layoutWidths.contentPane} align={'stretch'}>
              <Spacer flex={'auto'} maxWidth={layoutWidths.contentPane-layoutWidths.sidebar} space={'l'}>
                <Spacer space={'s'}>
                  {isFirstPart && (
                    <>
                      <Spacer space={'s'}>
                        <Spacer space={'xs'}>
                          <Spacer space={'xxxxs'}>
                            <Typography color={'primary'}>
                              <h5>
                                <a
                                  href={category_link}
                                  title={category}
                                  className={styles.category}
                                  onClick={() =>
                                    trackEvent(
                                      {
                                        ...CATEGORY_CLICKED,
                                        label: category,
                                      },
                                      { transport: 'beacon' }
                                    )
                                  }
                                >
                                  <strong>{category}</strong>
                                </a>
                              </h5>
                            </Typography>

                            <Typography>
                              <h1>{title}</h1>
                            </Typography>
                          </Spacer>

                          <div className={styles.bylineShares}>
                            <StoryByline
                              author={author}
                              created={created}
                              onAuthorClick={() =>
                                trackEvent(
                                  { ...AUTHOR_CLICKED, label: author.name },
                                  { transport: 'beacon' }
                                )
                              }
                            />
                            <ArticleSharing
                              link={link}
                              brand="sas"
                              onShare={network =>
                                trackEvent(
                                  { ...SHARE, label: network },
                                  { transport: 'beacon' }
                                )
                              }
                            />
                          </div>
                        </Spacer>

                        {!!youtube_video && (
                          <Video
                            video_id={youtube_video}
                            bg_url={large_image}
                            caption={image_caption}
                          />
                        )}

                        {!!(gallery && gallery.length) && !youtube_video && (
                          <Slider
                            brand={'sas'}
                            images={gallery}
                            aspectRatio={9 / 16}
                            minWidth={300}
                            maxWidth={751}
                            onPreviousSlide={() =>
                              trackEvent({
                                action: 'PREVIOUS SLIDE',
                                category: 'ARTICLE',
                                label: slug,
                              })
                            }
                            onNextSlide={() =>
                              trackEvent({
                                action: 'NEXT SLIDE',
                                category: 'ARTICLE',
                                label: slug,
                              })
                            }
                            onGotoSlide={() =>
                              trackEvent({
                                action: 'GOTO SLIDE',
                                category: 'ARTICLE',
                                label: slug,
                              })
                            }
                          />
                        )}

                        {!!large_image &&
                          !youtube_video &&
                          !(gallery && gallery.length) && (
                            <MainImage
                              bg_url={large_image}
                              caption={image_caption}
                            />
                          )}
                      </Spacer>
                    </>
                  )}

                  <Spacer key={i} space={'s'}>
                    {chunksWithSeparators(
                      chunks,
                      !!ads.standard_inarticle_video
                    ).map(
                      (
                        {
                          pars: chunkPars,
                          separator,
                          separatorIndex,
                        }: ChunkWithSeparator,
                        j: number
                      ) => {
                        // Once the comments section is injected (it is the last
                        // content block), stop placing in-content ads — no ad should
                        // render at or after the comment section.
                        const isAfterComments =
                          commentsChunkIndex !== -1 && j >= commentsChunkIndex;

                        const recirculatedArticle =
                          separator === RECIRCULATED_ARTICLE &&
                          j !== chunks.length - 1
                            ? suggestedArticles[separatorIndex]
                            : undefined;

                        const nativeAd =
                          separator === NATIVE_AD
                            ? separatorIndex === 0
                              ? ads.standard_inarticle_native
                              : ads.standard_enterprise_inarticle_native
                            : undefined;

                        const videoAd =
                          separator === VIDEO_AD
                            ? ads.standard_inarticle_video
                            : undefined;

                        // No last-chunk guard. The display slots now sit at chunks 1-2,
                        // so on shorter articles they ARE the last chunk — the old
                        // `j !== chunks.length - 1` check would have suppressed them and
                        // left 9-16 paragraph articles with fewer ads than before. Video
                        // and native never had this guard, so this also makes the ad
                        // separators behave consistently. A display ad in the final chunk
                        // is still followed by the category cloud, sharing and comments —
                        // it is not the bottom of the page.
                        const displayAd =
                          separator === DISPLAY_AD
                            ? ads[`standard_inarticle${separatorIndex + 1}`]
                            : undefined;

                        return (
                          <React.Fragment key={j}>
                            <BlockUpdates
                              block
                              render={() => (
                                <Typography
                                  size={'l'}
                                  family={'copy'}
                                  dangerouslySetInnerHTML={{
                                    __html: chunkPars.join('\n'),
                                  }}
                                />
                              )}
                            />

                            {/*
                              No `j > 0` guard on the ad separators: the old guard
                              silently discarded whatever sat at index 0 of
                              chunkSeparatorOrder. The separator order is the single
                              source of truth for placement — chunk 0 is now the
                              out-stream video unit (which replaced the parallax
                              ScrollAd, 2026-07).

                              native + video are lazy and NOT predefined: on shorter
                              articles the later separators never render, and
                              predefining them put a phantom slot in the initial ad
                              request for a div that would never exist.
                            */}
                            {!isFirstPart && !isAfterComments && nativeAd && !hideAds && (
                              <div
                                className={`adNativeAd`}
                              >
                                <Ad
                                  ad={nativeAd}
                                  initialSize={['100%', 0]}
                                  gutter={'s'}
                                  lazy
                                  // backgroundColor={'transparent'}
                                />
                              </div>
                            )}

                            {!isFirstPart && !isAfterComments && videoAd && !hideAds && (
                              <div
                                className={`adVideoAd`}
                              >
                                <Ad
                                  ad={videoAd}
                                  gutter={'s'}
                                  lazy
                                  // backgroundColor={'transparent'}
                                />
                              </div>
                            )}

                            {/* Commented out per request — DISPLAY_AD in-content
                              units (adDisplayAd: standard_inarticle1/2). These are
                              lazy and not predefined, so no AdSetup change is needed;
                              the DISPLAY_AD entries in chunkSeparatorOrder now simply
                              render nothing. */}
                            {/* {!isFirstPart && !isAfterComments && displayAd && !hideAds && (
                              <div
                                className={`adDisplayAd`}
                              >
                                <Ad
                                  ad={displayAd}
                                  gutter={'s'}
                                  initialSize={[300, 90]}
                                  lazy
                                />
                              </div>
                            )} */}
                          </React.Fragment>
                        );
                      }
                    )}
                  </Spacer>
                </Spacer>

                {isSecondPart && (
                  <>
                    <div className={styles.cloudShares}>
                      <div className={styles.clouds}>
                        {categories.map(({ name, link }) => (
                          <a
                            key={name}
                            href={link}
                            title={name}
                            target={'_blank'}
                            className={styles.cloud}
                            onClick={() =>
                              trackEvent(
                                { ...CATEGORY_CLOUD_CLICKED, label: name },
                                { transport: 'beacon' }
                              )
                            }
                          >
                            {name}
                          </a>
                        ))}
                      </div>

                      <ArticleSharing
                        link={link}
                        brand="sas"
                        onShare={network =>
                          trackEvent(
                            { ...SHARE, label: network },
                            { transport: 'beacon' }
                          )
                        }
                      />
                    </div>

                    {showLinkbyPubFeed && (
                      <Spacer className={styles.linkbyContainer} space={'s'}>
                        <Spacer relative row space={'m'} align={'stretch'}>
                          <Spacer flex={'auto'} width={'100%'} space={'m'}>
                          {process.env.COOKIE_NAME !== 'id_token' ? 
                            <img src="https://placehold.co/500x250/f3f3f3/000?text=Linkby+Widget" />
                            :
                            <div className="linkby-widget" data-type="listicle"></div> 
                          }
                          </Spacer>
                        </Spacer>
                      </Spacer>
                    )}
                  </>
                )}
              </Spacer>

              <Spacer className={styles.lOnly} width={layoutWidths.sidebar} relative>
                {isFirstPart && ads.standard_siderail1 && !hideAds && (
                  <Ad
                    ad={ads.standard_siderail1}
                    sticky
                    inline
                    align={'flex-end'}
                    backgroundColor={'transparent'}
                    renderMatch={'l'}
                    initialSize={[300, 250]}
                  />
                )}

                {!isFirstPart &&
                  isSecondPart &&
                  ads.standard_siderail2 &&
                  !hideAds && (
                    <Ad
                      ad={ads.standard_siderail2}
                      sticky
                      lazy
                      align={'flex-end'}
                      backgroundColor={'transparent'}
                      renderMatch={'l'}
                      initialSize={[300, 250]}
                    />
                  )}
              </Spacer>
            </Spacer>

            {/*
              No `!isSecondPart` guard. On a split article this is unchanged — the
              billboard renders once, after the lede — because isSecondPart is false on
              part 0. But an article too short to split has ONE part that is both first
              and last, and that guard was suppressing the billboard there while
              Article.tsx still predefined it: an ad request for a div that never
              existed. Dropping it means standard_billboard now renders on every
              standard article, so predefining it is always valid.
            */}
            {/* Commented out per request — standard_billboard (adStandardBillboard).
              Its predefine in Article.tsx AdSetup is commented out to match, so we
              don't request an ad for a div that no longer renders. */}
            {/* {isFirstPart &&
              ads.standard_billboard &&
              !hideAds && (
                <Ad
                  className={`adStandardBillboard`}
                  ad={ads.standard_billboard}
                  initialSize={[300, 90]}
                  predefined
                  inline
                />
              )
            } */}
            
            {/* {isFirstPart &&
              !isSecondPart &&
              ads.standard_inarticle_video &&
              !hideAds && (
                <Ad
                  ad={ads.standard_inarticle_video}
                  gutter={'s'}
                  inline
                  predefined
                  backgroundColor={'transparent'}
                />
              )
            } */}
          </Spacer>
        );
      })}

      {!hideAds && ads.standard_adhesion && <AdhesionAd ad={ads.standard_adhesion} />}

      <script
        data-cfasync={'false'}
        dangerouslySetInnerHTML={{
          __html: `
          window._dmTrack("product", "${title.replace(/"/, "'")}");
          `,
        }}
      />
    </Spacer>
  );
};

const standardArticleLayoutProps = memoize(
  suggestedArticlesSelector,
  suggestedArticles => ({
    suggestedArticles: suggestedArticles.slice(0, 4),
  })
);

export default compose(
  connect(standardArticleLayoutProps),
  styled(standardArticleLayoutStyles),
  onlyUpdateForKeys(['suggestedArticles'])
)(StandardArticleLayout);
